import React, { useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { WinExpectancyChart, GameLog, LoadingSkeleton, ErrorDisplay, KPIChips, GameHeader, GameLineups } from "../components/game";
import { useGameData } from "../hooks/useGameData";

const getTeamName = (name) => {
  if (!name || name === "nan" || String(name).toLowerCase() === "nan" || String(name).trim() === "") {
    return "Non-NCAA team";
  }
  return name;
};

const Card = ({ children }) => (
  <div className="bg-white p-3 sm:p-4 md:p-6 rounded-xl shadow-sm border border-gray-200">
    {children}
  </div>
);


const GamePage = () => {
  const { year, gameId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { gameData, loading, error } = useGameData(year, gameId);

  const { fallbackHomeId, fallbackAwayId } = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const homeId = params.get("homeId");
    const awayId = params.get("awayId");
    return {
      fallbackHomeId: homeId ? Number(homeId) : null,
      fallbackAwayId: awayId ? Number(awayId) : null,
    };
  }, [location.search]);

  const handleBack = () => {
    if (gameData?.game_date) {
      navigate(`/scoreboard?date=${gameData.game_date}&division=${gameData.division}`);
    } else {
      navigate("/scoreboard");
    }
  };

  if (loading) return <LoadingSkeleton />;
  if (error) return <ErrorDisplay error={error} />;
  if (!gameData) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 relative overflow-hidden">
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-[6%] right-[8%] w-[380px] h-[380px] bg-gradient-to-r from-blue-400/20 to-indigo-400/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-[12%] left-[6%] w-[520px] h-[520px] bg-gradient-to-r from-purple-400/15 to-pink-400/15 rounded-full blur-3xl animate-pulse delay-1000" />
        <div className="absolute top-[48%] right-[28%] w-[300px] h-[300px] bg-gradient-to-r from-cyan-400/20 to-blue-400/20 rounded-full blur-2xl animate-pulse delay-500" />
        <div className="absolute top-[18%] left-[18%] w-[220px] h-[220px] bg-gradient-to-r from-indigo-400/25 to-purple-400/25 rounded-full blur-xl animate-pulse delay-700" />
      </div>
      <div className="relative z-10 container max-w-6xl mx-auto px-8 sm:px-12 lg:px-16 py-16">
        <GameHeader gameData={gameData} onBack={handleBack} fallbackHomeId={fallbackHomeId} fallbackAwayId={fallbackAwayId} />
        <KPIChips gameData={gameData} />
        <div className="space-y-4">
          <div className="h-px bg-gray-200" />
          <Card>
            <WinExpectancyChart homeTeam={getTeamName(gameData.home_team)} awayTeam={getTeamName(gameData.away_team)} homeTeamId={gameData.home_team_id || fallbackHomeId} awayTeamId={gameData.away_team_id || fallbackAwayId} plays={gameData.plays || []} />
          </Card>
          <div className="h-px bg-gray-200" />
          <Card>
            <div className="rounded-lg sm:rounded-xl">
              <GameLog plays={gameData.plays || []} homeTeam={getTeamName(gameData.home_team)} awayTeam={getTeamName(gameData.away_team)} />
            </div>
          </Card>
          <div className="h-px bg-gray-200" />
          <Card>
            <GameLineups 
              battingLineups={gameData.batting_lineups || []} 
              pitchingLineups={gameData.pitching_lineups || []}
              homeTeam={getTeamName(gameData.home_team)}
              awayTeam={getTeamName(gameData.away_team)}
            />
          </Card>
        </div>
      </div>
    </div>
  );
};

export default GamePage;
